import { useEffect, useState } from "react";
import {
  Card,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Modal,
  Select,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { addDoc, collection, getFirestore } from "@firebase/firestore";
import { addBehaviourTimelineEntries } from "framework/students/profile";
import { toYMD } from "utils/date";

const CustomFormControl = styled(FormControl)(({ theme }) => {
  const { palette } = theme;
  return {
    "& .MuiOutlinedInput-root": {
      height: "44px",
      "& fieldset": {
        border: "1px solid #bababa",
      },
    },
    "& .MuiOutlinedInput-root.Mui-error": {
      "& fieldset": {
        border: `2px solid ${palette.error.main}`,
      },
      "&:hover fieldset": {
        border: `2px solid ${palette.error.main}`,
      },
    },
  };
});

const modalStyle = {
  minWidth: 350,
  maxWidth: 833.33,
  boxShadow: 24,
  p: 4,
  m: 1,
};

const firestore = getFirestore();

const AddTimeLineModal = ({
  admissionNo,
  open,
  closeModal,
  setSnackbar,
  fetchBehaviourTimeline,
}) => {
  const [formState, setFormState] = useState({
    note: "",
    type: "",
    refNo: "",
    date: "0001-01-01",
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setFormState({
      note: "",
      type: "",
      refNo: "",
      date: toYMD(new Date()),
    });
  }, [open]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!admissionNo) {
      setSnackbar({
        type: "error",
        icon: "error",
        desc: "Can't access student info for whom which you are trying to add behaviour timeline.",
      });
      return;
    }
    if (formState.date === "0001-01-01") {
      setSnackbar({ type: "error", icon: "error", desc: "Fill all fields." });
      return;
    }
    try {
      setIsLoading(true);
      const timelineEntry = { ...formState, admissionNo };
      timelineEntry.date = new Date(timelineEntry.date);
      const currTime = new Date();
      timelineEntry.date.setHours(
        currTime.getHours(),
        currTime.getMinutes(),
        currTime.getSeconds(),
        currTime.getMilliseconds()
      );
      await addBehaviourTimelineEntries(timelineEntry);
      fetchBehaviourTimeline();
      setSnackbar({
        type: "success",
        icon: "check",
        desc: "Successfully added behaviour timeline.",
      });
      closeModal();
    } catch (err) {
      setSnackbar({
        type: "error",
        icon: "error",
        desc: "Failed to add behaviour timeline.",
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <>
      <Modal
        open={isLoading || open}
        onClose={closeModal}
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <Card sx={modalStyle}>
          <MDTypography variant="h5">Add Timeline Entry</MDTypography>
          <Divider sx={{ height: "2px" }} />
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2} mb={5} mt={1}>
              <Grid item xs={12} md={6} lg={6} minWidth={300}>
                <MDInput
                  fullWidth
                  required
                  label="Note"
                  value={formState.note}
                  onChange={(e) => {
                    setFormState((prev) => ({ ...prev, note: e.target.value }));
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={6}>
                <CustomFormControl fullWidth required>
                  <InputLabel id="type-select-label">Type</InputLabel>
                  <Select
                    labelId="type-select-label"
                    id="type-select"
                    label="Type"
                    value={formState.type}
                    onChange={(e) => {
                      setFormState((prev) => {
                        return { ...prev, type: e.target.value };
                      });
                    }}
                  >
                    <MenuItem value="pos">Positive Timeline</MenuItem>
                    <MenuItem value="neg">Negative Timeline</MenuItem>
                    <MenuItem value="nut">Neutral Timeline</MenuItem>
                  </Select>
                </CustomFormControl>
              </Grid>
              <Grid item xs={12} md={6} lg={6} minWidth={300}>
                <MDInput
                  fullWidth
                  required
                  label="Reference No"
                  value={formState.refNo}
                  onChange={(e) => {
                    setFormState((prev) => {
                      return { ...prev, refNo: e.target.value };
                    });
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={6}>
                <MDInput
                  required
                  fullWidth
                  type="date"
                  label="Date"
                  value={formState.date}
                  onChange={(e) => {
                    setFormState((prev) => {
                      return { ...prev, date: e.target.value };
                    });
                  }}
                />
              </Grid>
            </Grid>
            <Divider sx={{ height: "2px" }} />
            <Grid container spacing={2}>
              <Grid item xs={0} md={2} lg={6} />
              <Grid item xs={12} md={5} lg={3}>
                <MDButton
                  fullWidth
                  variant="gradient"
                  color="error"
                  sx={{ height: "40px" }}
                  disabled={isLoading}
                  onClick={closeModal}
                >
                  Cancel
                </MDButton>
              </Grid>
              <Grid item xs={12} md={5} lg={3}>
                <MDButton
                  type="submit"
                  fullWidth
                  variant="gradient"
                  color="success"
                  sx={{ height: "40px" }}
                  disabled={isLoading}
                >
                  {isLoading ? <CircularProgress /> : "Add"}
                </MDButton>
              </Grid>
            </Grid>
          </form>
        </Card>
      </Modal>
    </>
  );
};

export default AddTimeLineModal;
